import {useEffect, useState} from "react";
import CollaboratorAnnualReportForm from "./CollaboratorAnnualReportForm";
import ApplicantAnnualReportForm from "./ApplicantAnnualReportForm";
import {NavLink, useNavigate} from "react-router-dom";
import ProjectInstitute from "../model/ProjectInstitute";
import AnnualReportForm from "../model/AnnualReportForm";
import {Toast, ToastContainer} from "react-bootstrap";
import apiClient from "../../../framework/web/ApiClient";

const SUBMIT_ANNUAL_REPORT_COMMUNITY_URL = "https://community.ukbiobank.ac.uk/hc/en-gb/articles/15013553517213-Submitting-an-annual-report";

const EXTRA_INFO = `Every 12 months, the Principal Investigator (PI) for every UK Biobank research project is required 
                  to provide information regarding their project and confirmation that they are complying with the terms 
                  of their Material Transfer Agreement (MTA). For help, please see our AMS User Guide. Failure to provide 
                  this report when requested will result in all researchers working on the project being prevented from 
                  accessing any UKB data. The ability to access data will be restored to all researchers working on the 
                  project when the report is submitted.`;

/**
 * Endpoint to fetch report information
 */
const FETCH_FORM_DATA_URL = "/ams/api/v1/annual-report-ui/open-report";
/**
 * Endpoint to save the report
 */
const SAVE_REPORT_URL = "/ams/api/v1/annual-report-ui/save-report";
/**
 * Endpoint to unlock a report
 */
const UNLOCK_REPORT_URL = "/ams/api/v1/annual-report-ui/unlock-report";
/**
 * Endpoint to approve a report
 */
const APPROVE_REPORT_URL = "/ams/api/v1/annual-report-ui/approve-report";

/**
 * This component is the main Annual Report Form page. It is used for both the Collaborator
 * and Applicant Reports.
 */
export default function CommonAnnualReportForm() {
   const [error, setError] = useState<string | null>(null);
   const [projectInstitute, setProjectInstitute] = useState<ProjectInstitute | null>(null);
   const [isSaving, setIsSaving] = useState(false);

   // Toast draft saved
   const [savedToastShow, setSavedToastShow] = useState(false);
   const [errorSavingShow, setErrorSaving] = useState(false);

   const navigate = useNavigate();

   // Load project info
   useEffect(() => {
      const [applicationId, instituteId, reportId] = getRequestedReport();

      const fetchReportData = async () => {
         try {
            // Prepare request URL
            let requestURL: string = `${FETCH_FORM_DATA_URL}?applicationId=${applicationId}&instituteId=${instituteId}`;
            if (reportId && reportId != "") {
               requestURL += `&reportId=${reportId}`;
            }

            const response = await apiClient.get(requestURL, {});
            if (response.status != 201) {
               setProjectInstitute(response.data.data);
            } else {
               setError(`Something went wrong. Response Status ${response.status}, Body ${response.statusText}`);
            }
         } catch (error) {
            // setError(`Failed to fetch menu: ${error}`);
            setProjectInstitute({
               ...projectInstitute,
               form: {
                  annualReportId: null,
                  applicationId: 0,
                  instituteId: 0,
                  dueDate: null,
                  submittedDate: null,
                  reportType: (requestedForm === 'applicant' ? 1 : 2),
                  locked: false,
                  approvedDate: null,
                  affiliates: null,
                  thirdPartyProcessors: null,
                  usePublicCodeRepo: null,
                  publicCodeRepos: null,
                  doiReferences: null,
                  codeReferences: null,
                  changedWhen: null,
                  changedWho: null
               }
            } as ProjectInstitute);
         }
      };
      fetchReportData();
   }, []);


   /**
    * Method to get the requested params from the URL.
    *
    * @returns A string array containing 3 values:
    *          <ol>
    *             <li>Application ID</li>
    *             <li>Institute ID</li>
    *             <li>Form ID - <i>This parameter is optional</i></li>
    *          </ol>
    */
   function getRequestedReport(): string[] {
      let queryParams = new URLSearchParams(window.location.search);

      // Get params
      let requestedProject = queryParams.get('applicationId');
      let requestedInstitute = queryParams.get('instituteId');
      let requestedReport = queryParams.get('reportId');

      return [requestedProject || "", requestedInstitute || "", requestedReport || ""];
   };

   /**
    * This method will read from the URL what type of form we are showing (Applicant or Collaborator)
    */
   function getRequestedForm(): string {
      let queryParams = new URLSearchParams(window.location.search);
      let requestedComponent = queryParams.get('form');
      return requestedComponent || '';
   };

   const requestedForm = getRequestedForm();

   /**
    * This method will format a string to correctly display the year the report is relevant to
    *
    * @param date The report due date
    */
   function renderYearLabel(date: Date): string {
      if (!date) {
         date = new Date();
      }
      let year = new Date(date).getFullYear();

      return `${year - 1}/${year - 2000}`;
   }

   if (error) {
      return <div>{error}</div>;
   }

   if (!projectInstitute) {
      return <div>Loading...</div>;
   }

   if (!projectInstitute.form) {
      // Create an empty form object as this is a new report
      setProjectInstitute({
         ...projectInstitute,
         form: {
            annualReportId: null,
            applicationId: projectInstitute.applicationId,
            instituteId: projectInstitute.instituteId,
            dueDate: projectInstitute.dueDate,
            submittedDate: null,
            reportType: 2,
            locked: false,
            approvedDate: null,
            affiliates: null,
            thirdPartyProcessors: null,
            usePublicCodeRepo: null,
            publicCodeRepos: null,
            doiReferences: null,
            codeReferences: null,
            changedWhen: null,
            changedWho: null
         }
      } as ProjectInstitute);
      return <></>;
   }

   /**
    * Method to handle the submission/saving of the report
    *
    * @param event The submit event
    * @param report The report form. This should be present in a state variable and up to date
    */
   const handleSubmit = (event: any, report: AnnualReportForm) => {
      setIsSaving(true);

      // Verify what button caused the 'submit event' to mark this request as either a 'save draft' or a 'submit report'
      const actionRequested = event.nativeEvent.submitter.id;

      if (actionRequested.includes('AnnualReportSubmitBtn') || actionRequested.includes('AnnualReportSaveBtn')) {
         const isSavedDraft = actionRequested.includes('AnnualReportSaveBtn');

         if (!isSavedDraft) {
            const form = event.currentTarget;
            if (form.checkValidity() === false) {
               form.classList.add('was-validated')

               event.preventDefault();
               event.stopPropagation();
               setIsSaving(false);
               return;
            }
         }

         // Handle the scenario where at first user adds external resources and then marks as no
         if (!report.usePublicCodeRepo) {
            report.publicCodeRepos = null;
         }

         event.preventDefault();
         if (report) {
            saveReport(report, isSavedDraft).then(() => setIsSaving(false));
         }
      } else {
         amtReportUpdate(report, actionRequested).then(() => setIsSaving(false));
      }
   };

   const amtReportUpdate = async (report: AnnualReportForm, action: string) => {
      try {
         // Prepare request URL
         let requestURL: string = (action.includes('AnnualReportApproveBtn')) ? APPROVE_REPORT_URL : UNLOCK_REPORT_URL;
         requestURL += `?reportId=${report.annualReportId}`;

         const response = await apiClient.put(requestURL, {});
         if (response.status != 201) {
            console.log("AMT Update done")
            navigate('annual-report?page=annual-report&action=amtReportUpdate');
         } else {
            console.log(`Something went wrong. Response Status ${response.status}, Body ${response.statusText}`);
            setErrorSaving(true);
         }
      } catch (error) {
         console.log(`Failed to fetch menu: ${error}`);
         setErrorSaving(true);
      }
   }

   const saveReport = async (reportForm: AnnualReportForm, isSavedDraft: boolean) => {
      try {
         // Prepare request URL
         let requestURL: string = SAVE_REPORT_URL;
         if (!isSavedDraft) {
            requestURL += `?isSubmit=1`;
         }

         const response = await apiClient.post(requestURL, reportForm);
         if (response.status != 201) {
            console.log("Saved")
            if (!isSavedDraft) {
               navigate('annual-report?page=annual-report&submit=success');
            } else {
               setSavedToastShow(true);
            }
         } else {
            console.log(`Something went wrong. Response Status ${response.status}, Body ${response.statusText}`);
            setErrorSaving(true);
         }
         // isSavingReport = false;
      } catch (error) {
         console.log(`Failed to fetch menu: ${error}`);
         setErrorSaving(true);
      }
   };

   return (<div>
      {/*Static area of the form*/}
      <NavLink to="?page=annual-report" className="nav-link return-to-landing">
         <i className="bi bi-arrow-left-short"></i>Return to Annual Reports
      </NavLink>
      <div id="collabAnnualReportStaticArea" className="form-static-area">
         <h2
            id="collabAnnualReportTitle">{`${renderYearLabel(projectInstitute.dueDate)} ${requestedForm === 'applicant' ? 'Applicant' : 'Collaborator'} Annual Report`}</h2>
         <div id="collabAnnualReportProjectInfo" className="project_info">
            <div id="collabAnnualReportProjectInfoStatic" className="project_info_static">
               <h5 id="collabAnnualReportProjectInfoStaticProjId">Project ID:</h5>
               <h5 id="collabAnnualReportProjectInfoStaticTitle">Title:</h5>
               <h5 id="collabAnnualReportProjectInfoStaticInstitute">Institute:</h5>
            </div>
            <div id="collabAnnualReportProjectInfoDynamic" className="project_info_dynamic">
               <h5 id="collabAnnualReportProjectInfoDynamicProjId">{projectInstitute.applicationId}</h5>
               <h5 id="collabAnnualReportProjectInfoDynamicTitle">{projectInstitute.title}</h5>
               <h5
                  id="collabAnnualReportProjectInfoDynamicInstitute">{projectInstitute.instituteId} - {projectInstitute.instituteName}</h5>
            </div>
         </div>
         <h6 id="collabAnnualReportExtraInfo">
            Guidance on completing the report is available via the UK Biobank Researcher Community: <a
            href={SUBMIT_ANNUAL_REPORT_COMMUNITY_URL}>Submitting an annual report.</a>
         </h6>
      </div>
      {requestedForm === 'applicant' &&
         <ApplicantAnnualReportForm projectInstitute={projectInstitute} isSaving={isSaving}
                                    handleSubmit={handleSubmit}/>}
      {requestedForm === 'collaborator' &&
         <CollaboratorAnnualReportForm projectInstitute={projectInstitute} isSaving={isSaving}
                                       handleSubmit={handleSubmit}/>}
      <ToastContainer id="collabAnnualReportToastContainer" position="bottom-end" className="p-3 toast-container">
         {/*Saved Draft toast*/}
         <Toast id="collabAnnualReportSavedDraftToast" onClose={() => setSavedToastShow(false)} show={savedToastShow}
                delay={3000} autohide>
            <Toast.Header id="collabAnnualReportSavedDraftToastHeader">
               <strong id="collabAnnualReportSavedDraftToastHeaderText" className="me-auto"><i
                  id="collabAnnualReportSavedDraftToastHeaderIcon"
                  className="bi bi-check-circle-fill text-success"/> Draft
                  Saved</strong>
            </Toast.Header>
            <Toast.Body id="collabAnnualReportSavedDraftToastBody">{`Report draft saved at ${
               new Date().toLocaleTimeString('en-GB', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
               })
            }`}</Toast.Body>
         </Toast>
         {/*Saved Draft toast*/}
         <Toast id="collabAnnualReportSavedDraftToast" onClose={() => setErrorSaving(false)} show={errorSavingShow}
                delay={5000} autohide>
            <Toast.Header id="collabAnnualReportSavedDraftToastHeader">
               <strong id="collabAnnualReportSavedDraftToastHeaderText" className="me-auto"><i
                  id="collabAnnualReportSavedDraftToastHeaderIcon"
                  className="bi bi-exclamation-circle-fill text-danger"/> Error</strong>
            </Toast.Header>
            <Toast.Body id="collabAnnualReportSavedDraftToastBody">Error saving the report. Please try again
               later</Toast.Body>
         </Toast>
      </ToastContainer>
   </div>);
}
